import clsx from "clsx";
import React, { DetailedHTMLProps, HTMLAttributes } from "react";

import { Cta, CtaProps } from "../../../buttons/cta";
import { ReactComponent as BottomShapeDesktop } from "../images/bottom-desktop.svg";
import { ReactComponent as BottomShapeMobile } from "../images/bottom-mobile.svg";
import { ParallaxBannerVariants } from "../types";

export interface BottomPanelProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  title: string;
  body?: string;
  cta?: CtaProps;
  variant: ParallaxBannerVariants;
}

export function BottomPanel({ title, body, cta, variant, ...divProps }: BottomPanelProps) {
  const panelBgClass = {
    "bg-neutral": variant === ParallaxBannerVariants.Neutral,
    "bg-primary": variant === ParallaxBannerVariants.Primary,
    "bg-transparent": variant === ParallaxBannerVariants.Light,
  };

  const svgClass = {
    "text-neutral": variant === ParallaxBannerVariants.Neutral,
    "text-primary": variant === ParallaxBannerVariants.Primary,
    "text-transparent": variant === ParallaxBannerVariants.Light,
  };

  const titleClass = {
    "text-primary": variant === ParallaxBannerVariants.Neutral,
    "text-neutral":
      variant === ParallaxBannerVariants.Primary || variant === ParallaxBannerVariants.Light,
  };

  const bodyClass = {
    "text-neutral":
      variant === ParallaxBannerVariants.Primary || variant === ParallaxBannerVariants.Light,
  };

  return (
    <div {...divProps}>
      <BottomShapeDesktop className={clsx("hidden md:block absolute top-0 left-0", svgClass)} />
      <BottomShapeMobile className={clsx("md:hidden absolute top-0 left-0", svgClass)} />
      <div className="flex h-fit relative">
        <div className="hidden md:flex flex-col w-[45%]">
          <div className="mt-[100%]" />
          <div className={clsx("flex-[1_0_auto] mr-[-1px]", panelBgClass)} />
        </div>
        <div
          className={clsx(
            "w-full md:w-[55%] xl:w-[60%]", // width
            "mt-[33%] md:mt-12 xl:mt-16",
            "p-8 pt-0 md:pt-8 3xl:pr-[20%]", // padding
            panelBgClass
          )}
        >
          <h3
            className={clsx(
              "flex-shrink-0",
              "text-4xl xl:text-5xl 2xl:text-5xl", // font size
              titleClass
            )}
          >
            {title}
          </h3>
          <div
            className={clsx(
              "mt-4 xl:mt-12 2xl:mt-16", // margin
              "text-lg xl:text-2xl 2xl:text-3xl", // font size
              "font-thin",
              bodyClass
            )}
          >
            {body}
          </div>
          {cta && (
            <div
              className={clsx(
                "flex-shrink-0",
                "mt-8 xl:mt-12 2xl:mt-16" // margin
              )}
            >
              <Cta
                {...cta}
                variant={variant === ParallaxBannerVariants.Primary ? "light" : "primary"}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
